import * as React from "react"
import { features } from "../../../Shared/features"
import { getCookie, setCookie } from "../../../Shared/utils/cookies"
import { acceptCookies, CookieStatus, denyCookies } from "../../../Shared/utils/datalayer.mapping"
import { Link } from "./library/link"
import { trackCookiesAccepted, trackCookiesDenied } from "./library/tracking"

type CookiebarProps = {
  status: CookieStatus
  onChange: CookieUpdater
}

type CookiebarState = {
  height: number
  expanded: boolean
  videoCookiesAccepted: boolean;
}

type CookieType = {
  kind: 'video' | 'functional' | 'analytical' | 'tracking',
  label: string,
  toggleCookie?: () => void,
  isAccepted: boolean
}

// FramedCookiebar() does not render <Cookiebar/> when the website is being displayed 
// in an iframe. This is because the website is iframed in public librarian computers
// who do not support cookies. Origin of iframe: https://cmsv2.beesmart.nl/.
export const FramedCookiebar = (props: CookiebarProps): JSX.Element => (
  window.self === window.top
    ? <Cookiebar {...props} />
    : <></>
)

export class Cookiebar extends React.Component<CookiebarProps, CookiebarState> {
  private cookiebar: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>()

  constructor(props: CookiebarProps) {
    super(props)
    this.state = {
      height: 0,
      expanded: false,
      videoCookiesAccepted: getCookie("video-cookies") === "yes",
    }
  }

  updateHeight() {
    this.setState(_ => ({
      height: this.cookiebar.current ? this.cookiebar.current.clientHeight : 0,
    }))
  }

  componentDidUpdate(prevProps: CookiebarProps, prevState: CookiebarState) {
    this.updateHeight()
  }

  componentDidMount() {
    this.updateHeight()
    window.addEventListener("resize", () => this.updateHeight())
  }

  componentWillUnmount() {
    window.removeEventListener("resize", () => this.updateHeight())
  }

  shouldComponentUpdate(nextProps: CookiebarProps, nextState: CookiebarState) {
    return (
      this.state.height !== nextState.height ||
      this.props.status !== nextProps.status ||
      this.state.expanded !== nextState.expanded ||
      this.state.videoCookiesAccepted !== nextState.videoCookiesAccepted
    )
  }

  toggleExpand = () => {
    this.setState(prevState => ({ expanded: !prevState.expanded }))
  }

  toggleVideoCookies = () => {
    const newValue = !this.state.videoCookiesAccepted;
    setCookie("video-cookies", newValue ? "yes" : "no", 365);
    this.setState({ videoCookiesAccepted: newValue });
  };

  savePreferences = () => {
    if (this.state.videoCookiesAccepted) {
      acceptCookies()
    } else {
      denyCookies()
    }
    this.props.onChange("accepted")
  }

  render() {
    let p = this.props
    const { expanded } = this.state;

    const items: CookieType[] = [
    {
      kind: 'functional',
      label: 'Functioneel. Deze cookies zijn nodig om de website goed te laten werken.',
      isAccepted: true
    },
    {
      kind: 'analytical',
      label: 'Analytisch. Met deze cookies analyseren we het gebruik van de website. We slaan geen persoonsgegevens op.',
      isAccepted: true
    },
    {
      kind: 'video',
      label: "Video's. Met deze cookies spelen we video's af op de website. (optioneel)",
      toggleCookie: this.toggleVideoCookies,
      isAccepted: this.state.videoCookiesAccepted
    },]

    return p.status != "accepted" && p.status != "denied" ? (
      <div style={this.state.height !== 0 ? { minHeight: this.state.height } : {}}>
        <div className="cookiebar" role="alert">
          <div className="cookiebar__inner container--mg align--center" ref={this.cookiebar}>
            <h2 className="sr-text">Cookiebar</h2>
            <p className="text--s text--styled">
              Om onze website zo goed mogelijk te gebruiken, is het nodig dat je onze cookies accepteert. Dat klinkt
              makkelijk en dat is het ook. Door op accepteren te klikken, geef je ons toestemming om cookies te
              plaatsen. Wil je dit liever niet? Dan kun je ervoor kiezen om alleen akkoord te gaan met minimale cookies.
              Hiermee analyseren we onze website en daarbij slaan we geen persoonsgegevens op. Lees voor meer informatie
              onze{" "}
              <Link url={features().privacyStatementUrl} className="cookiebar__link">
                privacyverklaring
              </Link>
              .
            </p>
            <div className="cookiebar__actions">
              <div>
              <button
                onClick={e => {
                  e.preventDefault()
                  acceptCookies()
                  trackCookiesAccepted()
                  this.setState({ videoCookiesAccepted: true });
                  p.onChange("accepted")
                  window.location.reload();
                }}
                className="button button--primary"
              >
                <span className="button__label">Cookies accepteren</span>
              </button>
              <button
                onClick={e => {
                  e.preventDefault()
                  denyCookies()
                  trackCookiesDenied()
                  p.onChange("denied")
                  window.location.reload();
                }}
                className="button white"
              >
                <span className="button__label">Ik wil alleen minimale cookies</span>
              </button>
              </div>
              <a
                onClick={e => {
                  e.preventDefault()
                  this.toggleExpand()
                }}
                className="link cookiebar__link"
              >
                <span className="button__label">{expanded ? "Minder keuzes" : "Meer keuzes"}</span>
              </a>
            </div>

            {expanded && (
              <div className="cookiebar__extended">
                <p className="text--s text--styled">
                  Hier kun je gedetailleerde cookie-instellingen aanpassen. Kies welke soorten cookies je wilt accepteren:
                </p>
                <div className="cookiebar__extended--options">
                  {
                    items.map((item, index) => (
                      <div key={`tag__${index}`} className='form__check
                      '>
                        <input
                          id={`${item.kind}_${index.toString()}`}
                          className={'form__input--checkbox'}
                          type="checkbox"
                          checked={item.isAccepted}
                          name={item.kind}
                          value={item.label}
                          onChange={item.toggleCookie ? item.toggleCookie : undefined}
                          aria-describedby={'label-id'}
                        />
                        <label
                          id={`${item.kind}_label-id`}
                          className={'form__label--checkbox'}
                          htmlFor={`${item.kind}_${index.toString()}`}>
                          {item.label}
                        </label>
                      </div>
                    ))
                  }
                </div>

                <button onClick={this.savePreferences} className="button button--primary">
                  <span className="button__label">Instellingen opslaan</span>
                </button>

              </div>
            )}
          </div>
        </div>
      </div>
    ) : null
  }
}

export type CookieUpdater = (_: CookieStatus) => void

interface VideoCookieWrapperProps {
  videoUrl: string;
  videoName: string;
  showCookiebar: (visible: boolean) => void;
}

export const VideoCookieWrapper = ({ videoUrl, videoName, showCookiebar }: VideoCookieWrapperProps) => {
  const areVideoCookiesAccepted = getCookie("video-cookies") === "yes";

  if (!areVideoCookiesAccepted) {
    return (
      <div
        className="video-cookie-wrapper">
        <p className="text--s text--styled">Om deze video te bekijken, moet je video-cookies accepteren.</p>
        <button
          onClick={() => {
            acceptCookies();
            showCookiebar(true);
            window.location.reload();
          }}
          className="button button--primary"
        >
          Accepteer cookies
        </button>
      </div>
    );
  }

  return (
    <iframe
      key="iframe"
      className="block__iframe video-cookie-wrapper iframe"
      src={videoUrl}
      frameBorder="0"
      title={videoName}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );
};

type ChangeCookiesLinkProps = { onChange: CookieUpdater }

export const ChangeCookiesLink = (p: ChangeCookiesLinkProps): JSX.Element => (
  <a
    className="footer__link"
    href=""
    onClick={e => {
      e.preventDefault()
      p.onChange("changing")
    }}
  >
    Cookie-instellingen
  </a>
)

